import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import LoadingOverlay from "react-loading-overlay-ts";
import { fetchUserAttributes } from "../../../apis/dashboardApi.js";
import { Form, Row, Col, Button } from "react-bootstrap";
import DialogFooter from "./DialogFooter.js";
import { MultiSelect } from "primereact/multiselect";
import { buildDashboardModule, buildDashboardPayload } from "./dashboardPayloadConstants.js";

const CreateProjectView = ({
  initialSettings,
  onHide,
  isUpdateMode = false,
  projectCount,
  reloadDashboard,
  toastRef,
}) => {
  // Redux user info
  const { userInformation } = useSelector((state) => state.UserReducer);
  // PM and IL options as state
  const [pmOptions, setPmOptions] = useState([]);
  const [ilOptions, setIlOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const initialProjectTypes = initialSettings?.From
    ? initialSettings.From.split(",").map((s) => s.trim())
    : [];
  const [selectedProjectTypes, setSelectedProjectTypes] = useState(initialProjectTypes);
  // Helper to parse comma-separated string to array of objects for MultiSelect
  const parseMultiSelect = (value, options, separator = "$%") => {
    if (!value || !options.length) return [];
    const codes = value.split(separator).map((s) => s.trim()).filter(Boolean);
    return options.filter((opt) => codes.includes(opt.code));
  };

  const [selectedPM, setSelectedPM] = useState([]);
  const [selectedIL, setSelectedIL] = useState([]);

  // Fetch PM/IL options from API on mount
  useEffect(() => {
    const fetchPMOptions = async () => {
      setLoading(true);
      try {
        const userId = userInformation?.userid;
        const data = await fetchUserAttributes(userId);
        const result = Array.isArray(data.UserAttributes)
          ? data.UserAttributes
          : Array.isArray(data)
            ? data
            : [];
        if (result.length > 0) {
          // Collect unique PMs and ILs
          const uniquePMs = Array.from(new Set(result.map((item) => item.PM).filter(Boolean)));
          const uniqueILs = Array.from(new Set(result.map((item) => item.IL).filter(Boolean)));
          setPmOptions(uniquePMs.map((pm) => ({ name: pm, code: pm })));
          setIlOptions([
            { name: "No Value", code: "#" },
            ...uniqueILs.map((il) => ({ name: il, code: il })),
          ]);
        }
      } catch (error) {
        console.error("Error fetching PM options:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPMOptions();
  }, [userInformation?.userid]);

  // When options are loaded, initialize selectedPM/selectedIL from initialSettings.PM/IL if present
  useEffect(() => {
    if (initialSettings?.PM && pmOptions.length > 0 && selectedPM.length === 0) {
      setSelectedPM(parseMultiSelect(initialSettings.PM, pmOptions, ","));
    }
    if (initialSettings?.IL && ilOptions.length > 0 && selectedIL.length === 0) {
      setSelectedIL(parseMultiSelect(initialSettings.IL, ilOptions, "$%"));
    }
  }, [pmOptions, ilOptions]);

  // Utility to build payload for dashboard module view
  const buildDashboardModulePayload = () => {
    const userId = userInformation?.userid;
    const module = buildDashboardModule({
      moduleId: isUpdateMode ? initialSettings?.Module_ID || "" : "",
      moduleName: isUpdateMode ? initialSettings?.Title : `Projects_${projectCount + 1}`,
      moduleType: "Project",
      userId,
      config: {
        PM: selectedPM.length > 0 ? selectedPM.map((pm) => pm.code).join(",") : "",
        IL: selectedIL.length > 0 ? selectedIL.map((il) => il.code).join("$%") : "",
        From: selectedProjectTypes.join(","),
      },
      Sequence: isUpdateMode ? initialSettings?.Sequence : -1
    });
    return buildDashboardPayload({ module });
  };

  const handleCreateOrUpdate = async () => {
    setSubmitting(true);
    const payload = buildDashboardModulePayload();
    try {
      const { createOrUpdateDashboardModuleView } = await import("../../../apis/dashboardApi.js");
      await createOrUpdateDashboardModuleView(payload);
      if (toastRef && toastRef.current) {
        toastRef.current.show({
          severity: "success",
          summary: "Success",
          detail: isUpdateMode
            ? "Project view updated successfully"
            : "Project view created successfully",
          life: 3000,
        });
      }
      if (typeof reloadDashboard === "function") {
        reloadDashboard();
      }
      onHide();
    } catch (error) {
      console.error("Error creating/updating project module view:", error);
      if (toastRef && toastRef.current) {
        toastRef.current.show({
          severity: "error",
          summary: "Error",
          detail: `Failed to ${isUpdateMode ? "update" : "create"} project view.`,
          life: 5000,
        });
      }
      setSubmitting(false);
    }
  };

  const isFormValid =
    selectedProjectTypes.length > 0 && selectedPM.length > 0 && selectedIL.length > 0;

  return (
    <LoadingOverlay active={loading} spinner text="Loading user attributes...">
      {!loading && (
        <>
          <Row className="project-form">
            <Col md={4}>
              <Form.Group className="d-flex align-items-center gap-3">
                <Form.Label className="mb-0 module-form-label" style={{ whiteSpace: "nowrap" }}>
                  From <span className="mandatory-asterisk"> *</span>
                </Form.Label>
                <div className="d-flex flex-column">
                  {["My Projects", "All Projects"].map((type) => (
                    <Form.Check
                      key={type}
                      type="checkbox"
                      id={type.replace(/\s/g, "")}
                      name={type}
                      label={type}
                      checked={selectedProjectTypes.includes(type)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedProjectTypes((prev) => [...prev, type]);
                        } else {
                          setSelectedProjectTypes((prev) => prev.filter((t) => t !== type));
                        }
                      }}
                    />
                  ))}
                </div>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="d-flex align-items-center gap-2">
                <Form.Label className="mb-0 module-form-label" style={{ whiteSpace: "nowrap" }}>
                  PM <span className="mandatory-asterisk"> *</span>
                </Form.Label>

                <div style={{ minWidth: 0, flex: 1 }}>
                  <MultiSelect
                    value={selectedPM}
                    onChange={(e) => setSelectedPM(e.value)}
                    options={pmOptions}
                    optionLabel="name"
                    placeholder="Select PM"
                    maxSelectedLabels={3}
                    className="w-full"
                    filter
                  />
                </div>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group
                className="d-flex align-items-center gap-2"
                style={{ flex: 1, minWidth: 0 }}
              >
                <Form.Label className="mb-0 module-form-label" style={{ whiteSpace: "nowrap" }}>
                  IL <span className="mandatory-asterisk"> *</span>
                </Form.Label>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <MultiSelect
                    value={selectedIL}
                    onChange={(e) => setSelectedIL(e.value)}
                    options={ilOptions}
                    optionLabel="name"
                    placeholder="Select IL"
                    maxSelectedLabels={3}
                    className="w-full"
                    filter
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>

          <DialogFooter
            submitting={submitting}
            isFormValid={isFormValid}
            isUpdateMode={isUpdateMode}
            onCancel={onHide}
            onSubmit={handleCreateOrUpdate}
          />
        </>
      )}
    </LoadingOverlay>
  );
};

export default CreateProjectView;
